import { FC } from "react";
import { ArrowUpRight, FileText, Loader2, SquareArrowOutUpRight } from "lucide-react";
import { Citation } from "@/constants/citation.constant";
import clsx from "clsx";

/**
 * Enum for SourceItem variants
 */
export enum SourceItemVariant {
  DEFAULT = "default",
  COMPACT = "compact",
}

/**
 * Props for the SourceItem component
 */
interface Props {
  /** Citation source data to display */
  source: Citation;
  /** Index number of the source (displayed as [index]) */
  index: number;
  /** Callback function called when the source is clicked */
  onClick?: (citation: Citation) => void;
  /** Visual variant of the source item. Defaults to "default". */
  variant?: SourceItemVariant;
  /** Whether a citation is currently loading */
  isCitationLoading?: boolean;
  /** ID of the currently selected citation */
  selectedCitationId?: number | null;
}

/**
 * SourceItem component displays an individual citation source in the sources modal.
 * Supports two variants: default (with button) and compact (with icon). Shows loading
 * state when a citation is being fetched. Displays source file name and index.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {Citation} props.source - Citation source data
 * @param {number} props.index - Source index number
 * @param {(citation: Citation) => void} [props.onClick] - Callback fired when source is clicked
 * @param {SourceItemVariant} [props.variant="default"] - Visual variant: "default" | "compact"
 * @param {boolean} [props.isCitationLoading] - Whether citation is loading
 * @param {number | null} [props.selectedCitationId] - ID of selected citation
 * @returns {JSX.Element} Source item element with file info and action button/icon
 * 
 * @example
 * ```tsx
 * <SourceItem 
 *   source={citation} 
 *   index={1} 
 *   onClick={handleClick}
 *   variant="default"
 * />
 * ```
 */
export const SourceItem: FC<Props> = ({
  source,
  index,
  onClick,
  variant = SourceItemVariant.DEFAULT,
  isCitationLoading,
  selectedCitationId
}) => {
  const getSourceTitle = () => {
    if (source.source_file) {
      const decoded = decodeURIComponent(source.source_file);
      const fileName = decoded.split('/').pop() || decoded;
      return fileName.length > 100 ? fileName.substring(0, 100) + '...' : fileName;
    }
    return `Source ${index}`;
  };

  const sourceTitle = getSourceTitle();

  return (
    <div
      className={clsx(
        "border border-[#D3E2F8] dark:border-gray-700 rounded-lg transition-colors",
        variant === SourceItemVariant.COMPACT && "px-4 pb-0 hover:bg-gray-50 dark:hover:bg-gray-800"
      )}
    >
      {variant === SourceItemVariant.DEFAULT ? (
        <button
          onClick={() => onClick?.({ name: source.source_file, ...source })}
          className="w-full flex items-start gap-3 px-4 py-3 text-left bg-primary-50 hover:bg-[#e8effa] rounded-lg transition-colors"
          aria-label={`View full citation for ${sourceTitle}`}
        >
          <div className="text-xs font-medium text-[#09090B] dark:text-gray-100 flex-1 min-w-0">
            [{index}] {sourceTitle}
          </div>
          <div className="flex-shrink-0 mt-0.5">
            {isCitationLoading && selectedCitationId == source.id ? (
              <Loader2 size={20} className="text-text-500 animate-spin" aria-label="Loading citation" />
            ) : (
              <SquareArrowOutUpRight size={20} className="text-black/95" />
            )}
          </div>
        </button>
      ) : (
        <div className="flex items-center justify-start gap-3">
          <div className="flex-shrink-0">
            <FileText size={20} className="text-black/95" />
          </div>
          <div className="text-sm font-medium text-[#09090B] dark:text-gray-100 py-2 truncate">
            [{index}] {sourceTitle}
          </div>
          <div
            onClick={() =>
              !isCitationLoading &&
              onClick?.({ name: source.source_file, ...source })
            }
            className="flex-shrink-0 cursor-pointer ml-auto"
          >
            {isCitationLoading && selectedCitationId == source.id ? (
              <Loader2 size={16} className="text-text-500 animate-spin" aria-label="Loading citation" />
            ) : (
              <ArrowUpRight size={16} className="text-text-500 hover:text-primary-500 transition" aria-label="View citation" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
